import { Button, Card, CardContent, Stack, Typography } from '@mui/material'
import { useState } from 'react'
import { EmphasizedNumber, InformationModal, SkeletonText } from 'utoniq-ui'

import { ArtistPaymentType } from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import { useMessage, useTranslation } from '@/core'
import { Cell } from '@/core/architecture'

import { Action, State } from './'

type RevenueCellProps = {
  salesReport: State['salesReport']
  transactions: State['transactions']
  onApply: Action['onApply']
  loading: State['loading']
  openingPayout: State['openingPayout']
  artist: State['artist']
}
export const RevenueCell: Cell<RevenueCellProps> = ({
  loading,
  openingPayout,
  onApply,
  salesReport,
  transactions,
  artist,
}) => {
  const { t } = useTranslation('consoleRevenue')
  const [open, setOpen] = useState<boolean>(false)
  const { showMessage } = useMessage()

  return (
    <Stack sx={{ px: 5, pb: 6, pt: 2, mb: 3 }}>
      {loading ? (
        <>
          <RevenueLoadingBoxCell
            title={t('totalSales')}
            labels={[
              t('store'),
              t('membership'),
              config.modules.liveStream ? t('tips') : undefined,
            ]}
          />
          <RevenueLoadingBoxCell
            title={t('transferableAmount')}
            labels={[t('currentSavings'), t('AmountInTheTransfer')]}
          />
        </>
      ) : (
        <>
          <RevenueBoxCell
            title={t('totalSales')}
            value={salesReport?.formattedTotalSales ?? ''}
            items={[
              {
                label: t('store'),
                value: salesReport?.store?.formattedTotalSales ?? '',
              },
              {
                label: t('membership'),
                value: salesReport?.subscription.formattedTotalSales ?? '',
              },
              config.modules.liveStream
                ? {
                    label: t('tips'),
                    value: salesReport?.tip.formattedTotalSales ?? '',
                  }
                : undefined,
            ]}
          />
          {!!transactions?.edges?.length && (
            <RevenueBoxCell
              title={t('transferableAmount')}
              value={
                transactions?.edges?.[0]?.node?.formattedTotalBalance ?? ''
              }
              items={[
                {
                  label: t('currentSavings'),
                  value: transactions?.edges?.[0]?.node?.formattedBalance ?? '',
                },
                {
                  label: t('AmountInTheTransfer'),
                  value:
                    transactions?.edges?.[0]?.node?.formattedBalanceInPayout ??
                    '',
                },
              ]}
            />
          )}
        </>
      )}
      <ListCell />
      {artist &&
        artist.paymentType !== ArtistPaymentType.Billing &&
        artist.status === 'active' && (
          <Button
            size="large"
            sx={{ width: 244, mx: 'auto' }}
            disabled={
              (transactions?.edges?.[0]?.node.formattedTotalBalanceNumber ??
                0) === 0
            }
            onClick={() => setOpen(true)}
          >
            {t('applyForTransfer')}
          </Button>
        )}
      <InformationModal
        open={open}
        onClose={() => setOpen(false)}
        title={t('titleConfirm')}
        content={t('contentConfirm')}
        cancelText={t('cancel')}
        buttons={
          <Button
            size="large"
            sx={{ width: 212 }}
            onClick={() => {
              onApply().then(() => {
                showMessage(t('transferCompleted'), 'success')
                setOpen(false)
              })
            }}
            loading={openingPayout}
          >
            {t('okConfirm')}
          </Button>
        }
      />
    </Stack>
  )
}
type RevenueBoxCell = {
  title: string
  value: string
  items: ({ label: string; value: string } | undefined)[]
}
const RevenueBoxCell: Cell<RevenueBoxCell> = ({ title, value, items }) => {
  const { t } = useTranslation('consoleRevenue')
  return (
    <Card elevation={0} sx={{ mb: 5 }}>
      <CardContent>
        <Typography variant="h4" sx={{ pb: 1 }}>
          {title}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1}>
          <EmphasizedNumber variant="h1">{value}</EmphasizedNumber>
          <Typography variant="h4">{t('yen')}</Typography>
        </Stack>
        <Stack sx={{ pt: 4 }}>
          <Stack spacing={2}>
            {items.map(
              (item, index) =>
                item && (
                  <Stack
                    key={index}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="caption">{item.label}</Typography>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <EmphasizedNumber variant="h6">
                        {item.value}
                      </EmphasizedNumber>
                      <Typography variant="subtitle2">{t('yen')}</Typography>
                    </Stack>
                  </Stack>
                )
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  )
}

type RevenueLoadingBoxCellProps = {
  title: RevenueBoxCell['title']
  labels: (string | undefined)[]
}
const RevenueLoadingBoxCell: Cell<RevenueLoadingBoxCellProps> = ({
  title,
  labels,
}) => (
  <Card elevation={0} sx={{ mb: 5 }}>
    <CardContent>
      <Typography variant="h4" sx={{ pb: 1 }}>
        {title}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={1}>
        <SkeletonText variant="medium" width="50%" />
      </Stack>
      <Stack sx={{ pt: 4 }}>
        <Stack spacing={2}>
          {labels.map(
            (label, index) =>
              label && (
                <Stack
                  key={index}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="caption">{label}</Typography>
                  <Stack direction="row" alignItems="center">
                    <SkeletonText variant="small" width={64} />
                  </Stack>
                </Stack>
              )
          )}
        </Stack>
      </Stack>
    </CardContent>
  </Card>
)

const ListCell: Cell = () => {
  const { t } = useTranslation('consoleRevenue')
  return (
    <Stack component="ul" spacing={3} sx={{ pl: 4, pb: 3, mb: 5, mt: 0 }}>
      {['note1', 'note2', 'note3', 'note4', 'note5'].map(note => (
        <Typography component="li" variant="caption" key={note}>
          {t(note)}
        </Typography>
      ))}
    </Stack>
  )
}
